import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { rolesObjType, User } from "../../types/api";
import loginBg from "../../assets/images/login-bg.svg";
import { useAuth } from "../../auth/AuthProvider";
import { usePermissionStore } from "../../store/usePermissionStore";
import { usePermissionSeedStore } from "../../store/usePermissionSeedStore";
import axiosInstance from "../../common/axiosInstance";

const Login: React.FC = () => {
  const [tenant_name, setTenantName] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const {
    isAuthenticated,
    setIsAuthenticated,
    setUser,
    setUserType,
    setRoleData,
    setOrgAdmin,
    setOrgUser,
  } = useAuth();
  const location = useLocation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      try {
        response = await axiosInstance.post(`/api/tenant/login`, {
          tenant_name,
          username,
          password,
        });
        try {
          if (response.status === 200 && response.data.user) {
            const user: User = response.data.user as User;
            const rolesData: rolesObjType = response.data
              ?.roleData as rolesObjType;

            if (response.data?.permissionSeed) {
              usePermissionSeedStore.getState().setSeed(response.data.permissionSeed);
            }

            if (rolesData && user?.roles.includes("orguser")) {
              const perms = response.data.roleData;
              setRoleData(perms);
              usePermissionStore
                .getState()
                .setPermissions(perms?.permissions || {});
            }

            if (user.roles.includes("orgadmin")) {
              setOrgAdmin(user as any);
            } else if (user.roles.includes("orguser")) {
              setOrgUser(user as any);
            }

            const userType = user?.roles.includes("orgadmin")
              ? "orgadmin"
              : user?.roles.includes("orguser")
                ? "orguser"
                : "guest";
            await handleSuccessfulLogin(user, userType);
            return;
          }
        } catch (e) {
          console.error("JS error after successful login", e);
        }
      } catch (orgAdminError: unknown) {
        try {
          response = await axiosInstance.post(`/api/admin/login`, {
            tenant_name,
            username,
            password,
          });

          if (response.status === 200 && response.data.admin) {
            const user: User = response.data.admin as User;
            if (response.data?.permissionSeed) {
              usePermissionSeedStore.getState().setSeed(response.data.permissionSeed);
            }
            await handleSuccessfulLogin(user, "superadmin");
            return;
          }
        } catch (superAdminError: unknown) {
          const getErrorMessage = (error: unknown): string => {
            if (axios.isAxiosError(error)) {
              return error.response?.data?.error || error.message;
            }
            return "Login failed. Please check your credentials.";
          };

          const errorMessage =
            [
              getErrorMessage(orgAdminError),
              getErrorMessage(superAdminError),
            ].find(
              (msg) => msg !== "Login failed. Please check your credentials."
            ) || "Login failed. Please check your credentials.";

          setError(errorMessage);
        }
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.error || "Login failed. Please try again."
        );
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSuccessfulLogin = async (
    user: User,
    userType: "orgadmin" | "superadmin" | "orguser" | "guest"
  ) => {
    setIsAuthenticated(true);
    setUser(user);
    setUserType(userType);

    if (userType === "orgadmin" && user.mustResetPassword) {
      navigate("/reset-password");
    } else {
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    if (isAuthenticated && location.pathname === "/login") {
      navigate("/dashboard");
    }
  }, [isAuthenticated]);

  return (
    <div className="container-fluid p-0 h-100">
      <div className="row no-gutters h-100 full-height">
        <div
          className="col-sm-7 d-none d-lg-flex bg"
          style={{ backgroundImage: "url('" + loginBg + "')" }}
        ></div>
        <div className="col-lg-5 bg-white">
          <div className="loginCont">
            <div className="row no-gutters h-100 align-items-center">
              <div className="col-sm-12 mx-auto">
                <h2>Login</h2>
                <p className="m-b-30">
                  Welcome back! Please enter your details
                </p>
                {error && <div className="alert alert-danger">{error}</div>}
                <form onSubmit={handleLogin}>
                  {/* Tenant Name */}
                  <div className="form-group">
                    <label
                      className="font-weight-semibold"
                      htmlFor="tenant_name"
                    >
                      Tenant Name
                    </label>
                    <div className="input-affix">
                      <i className="prefix-icon anticon anticon-user"></i>
                      <input
                        type="text"
                        className="form-control"
                        id="tenant_name"
                        value={tenant_name}
                        onChange={(e) => setTenantName(e.target.value)}
                        placeholder="Enter Tenant Name"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Username */}
                  <div className="form-group">
                    <label className="font-weight-semibold" htmlFor="userName">
                      Username
                    </label>
                    <div className="input-affix">
                      <i className="prefix-icon anticon anticon-user"></i>
                      <input
                        type="text"
                        className="form-control"
                        id="userName"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter Username"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="form-group">
                    <label className="font-weight-semibold" htmlFor="password">
                      Password
                    </label>
                    <div className="input-affix m-b-10">
                      <i className="prefix-icon anticon anticon-lock"></i>
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter Password"
                        disabled={loading}
                      />
                      <i
                        className={`suffix-icon anticon ${showPassword ? "anticon-eye" : "anticon-eye-invisible"
                          }`}
                        onClick={togglePasswordVisibility}
                        style={{ cursor: "pointer", color: "#999" }}
                      ></i>
                    </div>
                  </div>

                  {/* Remember Me and Forgot Password */}
                  <div className="form-group d-flex justify-content-between">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        value=""
                        id="flexCheckDefault"
                        disabled={loading}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="flexRadioDefault2"
                      >
                        Remember me
                      </label>
                    </div>
                    <Link
                      className="float-right forgotPassordLink"
                      to="/forgot-password"
                    >
                      Forget Password?
                    </Link>
                  </div>

                  {/* Submit Button */}
                  <div className="form-group">
                    <div className="d-flex align-items-center justify-content-between">
                      <button
                        type="submit"
                        className="btn btn-primary width100"
                        disabled={loading}
                      >
                        {loading ? "Signing In..." : "Sign In"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
